import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiSearch, FiArrowRight, FiCheckCircle, FiTruck, FiBookOpen, FiShoppingCart, FiStar } from "react-icons/fi";
import { FaWhatsapp, FaGem, FaPrayingHands } from "react-icons/fa";
import { GiIncense, GiPrayerBeads, GiMagicTrident, GiTempleGate, GiCrystalBall, GiSparkles } from "react-icons/gi";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import SectionTitle from "../components/SectionTitle";
import PageHero from "../components/PageHero";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";
import OmSymbol from "../components/OmSymbol";
import api, { imgUrl } from "../utils/api";
import { buildWhatsAppLink, productEnquiryMessage } from "../utils/whatsapp";
import { SAURABH } from "../utils/siteData";

// Category → decorative icon component shown on the filter chips & product fallback
const CATEGORY_ICON = {
  All: GiSparkles,
  "Puja Samagri": GiIncense,
  Gemstones: FaGem,
  Rudraksha: GiPrayerBeads,
  Yantra: GiMagicTrident,
  "Idols & Murti": GiTempleGate,
  Books: FiBookOpen,
  "Astrology Services": GiCrystalBall,
  Other: OmSymbol,
};

const CATEGORIES = Object.keys(CATEGORY_ICON);

// Trust band beneath the hero
const TRUST = [
  { icon: <FiCheckCircle />, title: "100% Authentic", desc: "Energized & shastra-approved items" },
  { icon: <FaWhatsapp />, title: "WhatsApp Enquiry", desc: "Direct chat, no online payment needed" },
  { icon: <FiTruck />, title: "Local Pickup", desc: "Thane & Mumbai delivery arranged" },
  { icon: <FaPrayingHands />, title: "Pandit-Guided", desc: "Right item for your specific need" },
];

export default function Marketplace() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    api
      .get("/products", { params: { category, search, page, limit: 12 } })
      .then((res) => {
        setProducts(res.data.products);
        setTotal(res.data.total ?? res.data.products.length);
        setPages(res.data.pages || 1);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, search, page]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    setPage(1);
  }, [category, search]);

  return (
    <div>
      <SEO
        title="Marketplace - Puja Samagri & Spiritual Products"
        description="Shop authentic puja samagri, gemstones, rudraksha, yantras, idols and spiritual books. Enquire directly on WhatsApp - no online payment needed."
        keywords="puja samagri online thane, rudraksha thane, gemstones thane, yantra thane, pooja items thane"
        path="/marketplace"
      />

      <PageHero
        eyebrow="Spiritual Shop"
        title="Spiritual Marketplace"
        subtitle="Authentic puja samagri, gemstones, rudraksha & more. Simply enquire on WhatsApp — no online payment required."
      />

      {/* TRUST BAND */}
      <section className="bg-maroon-900 bg-mandala text-white">
        <div className="max-w-7xl mx-auto px-4 relative z-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-gold-900/40">
          {TRUST.map((t) => (
            <div key={t.title} className="flex items-center gap-3 px-4 py-5">
              <span className="text-2xl flex-shrink-0 text-gold-300">{t.icon}</span>
              <div>
                <p className="font-heading text-gold-100 text-sm leading-tight">{t.title}</p>
                <p className="text-gold-100/60 text-xs">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-14 bg-saffron-50/60">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            eyebrow="Browse Products"
            title="Everything You Need for Your Rituals"
            subtitle="Handpicked, authentic spiritual items — each energized and selected with care for your pooja and remedies."
          />

          {/* FILTERS */}
          <div className="flex flex-col lg:flex-row gap-4 justify-between mb-8">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                    category === cat
                      ? "bg-saffron-500 text-white border-saffron-500 shadow-gold"
                      : "bg-white text-gray-700 border-gold-200 hover:border-saffron-400 hover:text-maroon-700"
                  }`}
                >
                  {(() => {
                    const Icon = CATEGORY_ICON[cat];
                    return Icon ? <Icon size={15} /> : null;
                  })()} {cat}
                </button>
              ))}
            </div>
            <div className="relative w-full lg:w-72">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-2.5 rounded-full border border-gold-200 bg-white focus:outline-none focus:ring-2 focus:ring-saffron-400 input-premium"
              />
            </div>
          </div>

          {/* RESULTS */}
          {loading ? (
            <Loader label="Loading products..." />
          ) : products.length === 0 ? (
            <EmptyState
              title="No products found"
              subtitle="Try a different category or search term, or check back soon for new products."
            />
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-5">
                Showing <span className="font-semibold text-maroon-700">{products.length}</span> of{" "}
                <span className="font-semibold text-maroon-700">{total}</span> products
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((p, i) => {
                  const isSale = p.discountPrice > 0;
                  const price = isSale ? p.discountPrice : p.price;
                  const off = isSale ? Math.round((1 - p.discountPrice / p.price) * 100) : 0;
                  const waLink = buildWhatsAppLink(SAURABH.whatsapp, productEnquiryMessage(p));

                  return (
                    <motion.div
                      key={p._id}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.45, delay: (i % 4) * 0.07 }}
                      className="group relative hover-lift gold-corner bg-white rounded-2xl shadow-premium border border-gold-100 overflow-hidden flex flex-col h-full"
                    >
                      <Link to={`/marketplace/${p.slug}`} className="flex flex-col h-full">
                        {/* Image with gradient overlay */}
                        <div className="relative aspect-square bg-gradient-to-br from-saffron-50 to-gold-100/60 overflow-hidden">
                          {p.images && p.images[0] ? (
                            <img
                              src={imgUrl(p.images[0])}
                              alt={p.name}
                              loading="lazy"
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                              {(() => {
                                const Icon = CATEGORY_ICON[p.category] || GiTempleGate;
                                return <Icon size={44} className="text-saffron-300 group-hover:scale-110 transition-transform duration-500" />;
                              })()}
                              <span className="text-xs text-saffron-400 font-medium">{p.category}</span>
                            </div>
                          )}

                          {/* Gradient overlay (always visible, deepens on hover) */}
                          <div className="absolute inset-0 bg-gradient-to-t from-maroon-900/70 via-maroon-900/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300" />

                          {/* Hover: title reveal on image */}
                          <div className="absolute bottom-3 left-3 right-3 translate-y-1 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                            <p className="text-white font-heading text-sm leading-tight drop-shadow-lg line-clamp-2">
                              {p.name}
                            </p>
                          </div>

                          {/* Badges */}
                          <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
                            {isSale && (
                              <span className="bg-red-600 text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow-lg">
                                {off}% OFF
                              </span>
                            )}
                            {p.featured && (
                              <span className="inline-flex items-center gap-1 bg-gradient-to-r from-gold-300 to-gold-500 text-maroon-900 text-[11px] font-bold px-2.5 py-1 rounded-full shadow">
                                <FiStar size={11} fill="currentColor" /> Featured
                              </span>
                            )}
                          </div>
                          {!p.inStock && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <span className="bg-white text-gray-800 text-xs font-bold px-3 py-1.5 rounded-full">
                                Out of Stock
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Body */}
                        <div className="p-5 flex flex-col flex-grow">
                          <span className="text-[11px] text-saffron-600 font-semibold uppercase tracking-wide">
                            {p.category}
                          </span>
                          <h4 className="font-heading text-maroon-800 text-sm sm:text-base mt-1 mb-2 line-clamp-2 group-hover:text-saffron-600 transition-colors">
                            {p.name}
                          </h4>
                          {p.shortDescription && (
                            <p className="text-xs text-gray-500 line-clamp-2 mb-4 leading-relaxed">{p.shortDescription}</p>
                          )}

                          {/* Price + CTA row */}
                          <div className="mt-auto pt-3 border-t border-gold-100/80">
                            <div className="flex items-end justify-between gap-2">
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <span className="text-xl font-bold text-maroon-700">₹{price}</span>
                                {isSale && (
                                  <span className="text-xs text-gray-400 line-through">₹{p.price}</span>
                                )}
                              </div>
                              <a
                                href={waLink}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.preventDefault()}
                                className="flex items-center gap-1 text-green-600 text-xs font-semibold hover:text-green-700 transition-colors"
                                title="Enquire on WhatsApp"
                              >
                                <FaWhatsapp size={14} /> Enquire
                              </a>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex justify-center gap-2 mt-10">
                  {[...Array(pages)].map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPage(idx + 1)}
                      className={`w-9 h-9 rounded-full text-sm font-medium transition-colors ${
                        page === idx + 1
                          ? "bg-maroon-700 text-gold-100"
                          : "bg-white border border-gold-200 text-gray-700 hover:border-saffron-400"
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* HELP STRIP */}
      <section className="py-14 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="rounded-3xl bg-hero-gradient bg-mandala text-white p-8 sm:p-10 text-center relative overflow-hidden shadow-premium-lg">
            <div className="absolute inset-0 bg-gold-radial pointer-events-none" />
            <div className="relative">
              <span className="text-3xl inline-flex justify-center"><FiShoppingCart size={32} /></span>
              <h3 className="font-heading text-2xl sm:text-3xl mt-2 mb-2">Not sure what you need?</h3>
              <p className="text-gold-100/85 max-w-xl mx-auto mb-6">
                Tell us about your pooja or remedy, and we'll guide you to the right samagri, gemstone or rudraksha —
                no guesswork.
              </p>
              <Link
                to="/contact"
                className="btn-gold inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold"
              >
                Get Free Guidance <FiArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
